/**
 * Real User Identity Management
 * 
 * Technische Accounts (shared devices) müssen sich ausweisen.
 * Die echte Benutzeridentität wird lokal gespeichert und bei Aktionen im Auditlog mitgeführt.
 */

export interface RealUserIdentity {
  id?: number;         // User-ID aus der API (wenn aus Liste gewählt)
  firstName: string;
  lastName: string;
  manual?: boolean;    // true wenn manuell eingegeben (Extern/Offline)
  confirmedAt: number; // Zeitpunkt der letzten Bestätigung
}

const STORAGE_KEY = 'realUserIdentity';

/**
 * Prüft ob der eingeloggte Benutzer ein technischer Account ist.
 * Erkennung über:
 * 1. localStorage-Flag (wird beim Login gesetzt)
 * 2. JWT-Payload: verschiedene mögliche Felder
 */
export const isTechnicalAccount = (): boolean => {
  // 1. Prüfe gespeichertes Flag (wird beim Login gesetzt)
  const storedFlag = localStorage.getItem('isTechnicalUser');
  if (storedFlag === 'true') return true;
  if (storedFlag === 'false') return false;
  
  // 2. Prüfe JWT-Payload
  const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  if (!accessToken) return false;
  
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    
    // Technischer User: typeID === 2
    if (payload.typeID === 2) return true;
    
    return false;
  } catch {
    return false;
  }
};

/**
 * Setzt das technische-Account-Flag explizit (z.B. nach Login aus Response-Daten).
 */
export const setTechnicalAccountFlag = (isTechnical: boolean): void => {
  localStorage.setItem('isTechnicalUser', isTechnical ? 'true' : 'false');
};

/**
 * Löscht das technische-Account-Flag (z.B. bei Logout).
 */
export const clearTechnicalAccountFlag = (): void => {
  localStorage.removeItem('isTechnicalUser');
};

/**
 * Gibt die gespeicherte echte Benutzeridentität zurück (oder null).
 */
export const getRealUserIdentity = (): RealUserIdentity | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

/**
 * Speichert die echte Benutzeridentität lokal.
 */
export const setRealUserIdentity = (identity: RealUserIdentity): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
};

/**
 * Löscht die gespeicherte Benutzeridentität.
 */
export const clearRealUserIdentity = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Gibt den Anzeigenamen der echten Benutzeridentität zurück.
 */
export const getRealUserDisplayName = (): string | null => {
  const identity = getRealUserIdentity();
  if (!identity) return null;
  return `${identity.firstName} ${identity.lastName}`.trim();
};

/**
 * Gibt die Benutzeridentität für Audit-Log Einträge zurück.
 * Falls technischer Account: echte Identität, sonst Token-Identität.
 */
const getTokenUserInfo = (): { name?: string; id?: number } => {
  const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
  if (!accessToken) return {};

  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const tokenUserId = typeof payload.id === 'number'
      ? payload.id
      : (typeof payload.userID === 'number' ? payload.userID : undefined);

    // Bevorzugt direkt aus JWT
    if (payload.firstName && payload.lastName) {
      return {
        name: `${payload.firstName} ${payload.lastName}`.trim(),
        id: tokenUserId,
      };
    }

    // Fallback: Name aus lokaler Active-Users-Liste
    const orgID = typeof payload.orgID === 'number' ? payload.orgID : undefined;
    if (orgID && tokenUserId !== undefined) {
      try {
        const localUsers = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
        const user = localUsers.find((u: any) => u.id === tokenUserId);
        if (user?.firstName && user?.lastName) {
          return {
            name: `${user.firstName} ${user.lastName}`.trim(),
            id: tokenUserId,
          };
        }
      } catch {}
    }

    // Letzter Fallback: Username
    if (payload.username) {
      return { name: String(payload.username), id: tokenUserId };
    }

    return { id: tokenUserId };
  } catch {
    return {};
  }
};

export const getAuditUserInfo = (): { realUser?: string; realUserId?: number; isTechnicalAccount: boolean } => {
  const tokenUser = getTokenUserInfo();
  
  if (!isTechnicalAccount()) {
    return {
      realUser: tokenUser.name,
      realUserId: tokenUser.id,
      isTechnicalAccount: false,
    };
  }

  const identity = getRealUserIdentity();
  if (identity) {
    return {
      realUser: `${identity.firstName} ${identity.lastName}`.trim(),
      realUserId: identity.id,
      isTechnicalAccount: true,
    };
  }

  // Technischer Account ohne bestätigte RealUserIdentity: Fallback auf Token-User
  return {
    realUser: tokenUser.name,
    realUserId: tokenUser.id,
    isTechnicalAccount: true,
  };
};

